/**
 * DataTableHeader Component
 *
 * Renders table header with sortable columns and select-all checkbox.
 */

import React from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { ColumnDefinition, SortState, SelectionState } from './types';

interface DataTableHeaderProps<T> {
  columns: ColumnDefinition<T>[];
  sortState?: SortState;
  onSortChange?: (sort: SortState) => void;
  selectable?: boolean;
  selectionState?: SelectionState;
  onSelectAll?: () => void;
  expandable?: boolean;
  hasActions?: boolean;
  actionsWidth?: string;
}

export function DataTableHeader<T>({
  columns,
  sortState,
  onSortChange,
  selectable = false,
  selectionState,
  onSelectAll,
  expandable = false,
  hasActions = false,
  actionsWidth = '160px'
}: DataTableHeaderProps<T>) {
  const handleSort = (column: ColumnDefinition<T>) => {
    if (!column.sortable || !onSortChange) return;

    const sortKey = column.sortKey || column.id;

    if (sortState?.column !== sortKey) {
      onSortChange({ column: sortKey, direction: 'asc' });
    } else if (sortState.direction === 'asc') {
      onSortChange({ column: sortKey, direction: 'desc' });
    } else if (sortState.direction === 'desc') {
      onSortChange({ column: null, direction: null });
    } else {
      onSortChange({ column: sortKey, direction: 'asc' });
    }
  };

  const getSortIcon = (column: ColumnDefinition<T>) => {
    if (!column.sortable) return null;

    const sortKey = column.sortKey || column.id;
    const isActive = sortState?.column === sortKey;

    return (
      <span className="ml-1 inline-flex flex-col">
        <ChevronUpIcon
          className={`h-3 w-3 -mb-1 ${
            isActive && sortState?.direction === 'asc'
              ? 'text-purple-600'
              : 'text-gray-400'
          }`}
        />
        <ChevronDownIcon
          className={`h-3 w-3 ${
            isActive && sortState?.direction === 'desc'
              ? 'text-purple-600'
              : 'text-gray-400'
          }`}
        />
      </span>
    );
  };

  return (
    <thead className="bg-gray-50 sticky top-0 z-20">
      <tr>
        {/* Expand column */}
        {expandable && (
          <th className="w-10 px-2 py-3" />
        )}

        {/* Checkbox column */}
        {selectable && (
          <th className="w-12 px-4 py-3 text-left">
            <input
              type="checkbox"
              checked={selectionState?.isAllSelected || false}
              ref={(el) => {
                if (el) {
                  el.indeterminate = selectionState?.isIndeterminate || false;
                }
              }}
              onChange={onSelectAll}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              title="Select all visible rows"
            />
          </th>
        )}

        {/* Data columns */}
        {columns.map(column => {
          // Build responsive visibility classes
          // hideOnMobile: hidden < 640px (table not shown there anyway, but for consistency)
          // hideOnTablet: hidden 640-1023px, show only on lg (≥1024px)
          const responsiveClass = column.hideOnTablet
            ? 'hidden lg:table-cell'  // Only show on desktop (≥1024px)
            : column.hideOnMobile
              ? 'hidden sm:table-cell' // Show on tablet+ (≥640px)
              : '';                     // Always visible

          // A sortable header is the table's only sort control, so it has to be reachable and has
          // to say which way it is sorted; a click handler on a bare `th` is neither.
          const interactive = !!column.sortable && !!onSortChange;
          const sortKey = column.sortKey || column.id;
          const active = interactive && sortState?.column === sortKey && !!sortState?.direction;

          return (
            <th
              key={column.id}
              scope="col"
              aria-sort={
                !interactive ? undefined
                  : active ? (sortState?.direction === 'asc' ? 'ascending' : 'descending')
                    : 'none'
              }
              tabIndex={interactive ? 0 : undefined}
              className={`
                px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider
                ${column.sortable ? 'cursor-pointer select-none hover:bg-gray-100' : ''}
                ${interactive ? 'focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500' : ''}
                ${column.headerClassName || ''}
                ${responsiveClass}
              `}
              style={{
                width: column.width,
                minWidth: column.minWidth,
                textAlign: column.align
              }}
              onClick={() => handleSort(column)}
              onKeyDown={interactive ? (event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                handleSort(column);
              } : undefined}
            >
              <div className={`flex items-center ${column.align === 'right' ? 'justify-end' : column.align === 'center' ? 'justify-center' : ''}`}>
                {column.header}
                {getSortIcon(column)}
              </div>
            </th>
          );
        })}

        {/* Actions column - sticky with fixed width */}
        {hasActions && (
          <th
            className="sticky right-0 top-0 z-30 px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-l border-gray-200 shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)]"
            style={{ width: actionsWidth, minWidth: actionsWidth }}
          >
            Actions
          </th>
        )}
      </tr>
    </thead>
  );
}

export default DataTableHeader;
